
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React from 'react';

interface MissionPageProps {
  onBack: () => void;
}

const MissionPage: React.FC<MissionPageProps> = ({ onBack }) => {
  return (
    <div className="min-h-dvh bg-[#050505] text-white font-sans selection:bg-emerald-500/30">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center text-white font-black text-lg shadow-[0_0_15px_rgba(16,185,129,0.4)]">
              Z
            </div>
            <span className="font-bold text-lg tracking-tight text-white">ZeroTrust</span>
          </button>
          
          <button 
            onClick={onBack}
            className="text-slate-400 hover:text-white transition-colors text-sm font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-indigo-600/20 rounded-full blur-[120px] pointer-events-none"></div>
        
        <div className="max-w-5xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-950/50 border border-indigo-500/30 text-indigo-300 text-xs font-bold uppercase tracking-widest mb-8">
            <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></span>
            Our Mission
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-[1.1] mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-200 to-slate-500">
            Mission Intelligence Team
          </h1>
          
          <p className="text-xl text-slate-400 leading-relaxed mb-12 max-w-3xl mx-auto">
            We're building the future of compliance education through immersive simulation. 
            Our mission is to make complex financial systems understandable, engaging, and accessible to everyone.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-slate-950">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-6 text-white">Our Core Mission</h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              Traditional compliance training is broken. It's boring, disconnected from reality, and fails to prepare 
              professionals for the complex challenges they'll face in the real world.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              We believe learning should be <span className="text-indigo-400 font-bold">interactive</span>, 
              <span className="text-emerald-400 font-bold"> engaging</span>, and 
              <span className="text-cyan-400 font-bold"> immediately applicable</span>. That's why we created 
              Compliance Command—a living simulation where you don't just read about compliance, you experience it.
            </p>
            <p className="text-lg text-slate-300 leading-relaxed">
              Every decision you make, every building you place, every crisis you navigate teaches you real-world 
              principles of risk management, regulatory compliance, and strategic thinking.
            </p>
          </div>
        </div>
      </section>

      {/* Intelligence Pillars */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Intelligence Pillars</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-indigo-950/50 to-purple-950/50 border border-indigo-500/20 rounded-2xl p-8 hover:border-indigo-500/40 transition-all">
              <div className="w-16 h-16 rounded-xl bg-indigo-500/20 flex items-center justify-center text-4xl mb-6">
                🎯
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Strategic Thinking</h3>
              <p className="text-slate-400 leading-relaxed">
                Learn to balance competing priorities, manage resources under pressure, and make decisions 
                with incomplete information—just like in the real world.
              </p>
            </div>

            <div className="bg-gradient-to-br from-emerald-950/50 to-teal-950/50 border border-emerald-500/20 rounded-2xl p-8 hover:border-emerald-500/40 transition-all">
              <div className="w-16 h-16 rounded-xl bg-emerald-500/20 flex items-center justify-center text-4xl mb-6">
                🛡️
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Risk Management</h3>
              <p className="text-slate-400 leading-relaxed">
                Understand how threats emerge, propagate, and can be mitigated. Experience the cascading 
                effects of security decisions in a safe, simulated environment.
              </p>
            </div>

            <div className="bg-gradient-to-br from-cyan-950/50 to-blue-950/50 border border-cyan-500/20 rounded-2xl p-8 hover:border-cyan-500/40 transition-all">
              <div className="w-16 h-16 rounded-xl bg-cyan-500/20 flex items-center justify-center text-4xl mb-6">
                🧠
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Adaptive Learning</h3>
              <p className="text-slate-400 leading-relaxed">
                Our intelligent system adapts to your playstyle, generating personalized challenges and 
                providing context-aware guidance that evolves with your skill level.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Values */}
      <section className="py-20 px-6 bg-black">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16 text-white">Our Values</h2>
          
          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center text-2xl">
                💡
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Education Through Experience</h3>
                <p className="text-slate-400">
                  We believe the best way to learn is by doing. Every feature is designed to teach through 
                  hands-on interaction, not passive consumption.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-emerald-500/20 flex items-center justify-center text-2xl">
                🌍
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Accessibility First</h3>
                <p className="text-slate-400">
                  Complex doesn't mean complicated. We make sophisticated concepts accessible to everyone, 
                  regardless of their background or experience level.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center text-2xl">
                🔬
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Continuous Innovation</h3>
                <p className="text-slate-400">
                  We're constantly pushing the boundaries of what's possible in educational technology, 
                  leveraging cutting-edge AI and simulation techniques.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center text-2xl">
                🤝
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Community Driven</h3>
                <p className="text-slate-400">
                  Your feedback shapes our roadmap. We're building this together with our community of 
                  learners, educators, and compliance professionals.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6 text-white">Join the Mission</h2>
          <p className="text-xl text-slate-400 mb-10">
            Ready to experience compliance training that actually works?
          </p>
          <button 
            onClick={onBack}
            className="px-10 py-4 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full transition-all shadow-[0_0_30px_rgba(79,70,229,0.4)] text-lg"
          >
            Start Your Journey →
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2 opacity-50">
            <div className="w-6 h-6 rounded bg-slate-800 flex items-center justify-center font-bold text-xs">Z</div>
            <span className="font-bold text-sm">ZeroTrust</span>
          </div>
          <div className="text-xs text-slate-600">
            © 2025 Compliance Command. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MissionPage;
